import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from '@emotion/styled';
import Tooltip from './Tooltip';
import { PanelRight, Columns2, Maximize2 } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation.jsx';

// ─── Context ─────────────────────────────────────────────────────

const DrawerContext = createContext(null);

/**
 * useDrawer — register tabs and control the drawer from any component.
 *
 * Usage:
 *   const { registerTab, unregisterTab, activeTab, openTab, closeDrawer } = useDrawer();
 */
export function useDrawer() {
  const ctx = useContext(DrawerContext);
  if (!ctx) throw new Error('useDrawer must be used within <DrawerProvider>');
  return ctx;
}

/** Like useDrawer, but returns null outside the provider (no throw). */
export function useDrawerOptional() {
  return useContext(DrawerContext);
}

export const TAB_BAR_WIDTH = 40;
export const DRAWER_WIDTH = 520;            // kept: 'normal' mode + back-compat; 420 → 520 on 27.10.2026 so a theme card and the chat read without wrapping every line

export const DRAWER_WIDTH_MODES = ['normal', 'wide', 'full'];
export const DRAWER_WIDTHS = {
  normal: { css: `min(${DRAWER_WIDTH}px, calc(100vw - ${TAB_BAR_WIDTH}px))`, px: 520 },
  wide:   { css: `min(720px, calc(100vw - ${TAB_BAR_WIDTH}px))`,             px: 720 },
  full:   { css: `min(max(1100px, 90vw), calc(100vw - ${TAB_BAR_WIDTH}px))`, px: 1100 },
};
export const DRAWER_WIDTH_MODE_KEY = 'kuutio-drawer-width-mode';
export const DRAWER_WIDTH_RESERVE_MAX = DRAWER_WIDTHS.wide.px;

const WIDTH_MODE_ICONS = {
  normal: PanelRight,
  wide: Columns2,
  full: Maximize2,
};

// ─── Provider ────────────────────────────────────────────────────

export function DrawerProvider({ children, initialWidthMode }) {
  const [tabs, setTabs] = useState([]); // [{ id, label, icon, color, order, render }]
  const [activeTab, setActiveTab] = useState(null);
  // Per-tab onboarding tips — small bubbles rendered next to the tab in the
  // rail when the tab isn't active. Lives in DrawerProvider so a tab owner
  // (e.g. ChatDrawerTab) can set/clear without re-registering the tab.
  const [tabTips, setTabTips] = useState({}); // { [tabId]: string | { text, dismissLabel, ... } | null }
  const [widthMode, setWidthModeState] = useState(() => {
    if (DRAWER_WIDTH_MODES.includes(initialWidthMode)) return initialWidthMode;
    try {
      const saved = localStorage.getItem(DRAWER_WIDTH_MODE_KEY);
      return DRAWER_WIDTH_MODES.includes(saved) ? saved : 'normal';
    } catch {
      return 'normal';
    }
  });

  const registerTab = useCallback((tab) => {
    setTabs(prev => {
      const existing = prev.find(t => t.id === tab.id);
      if (existing) return prev.map(t => t.id === tab.id ? { ...existing, ...tab } : t);
      return [...prev, tab].sort((a, b) => (a.order ?? 99) - (b.order ?? 99));
    });
  }, []);

  const unregisterTab = useCallback((tabId) => {
    setTabs(prev => prev.filter(t => t.id !== tabId));
    setActiveTab(prev => prev === tabId ? null : prev);
    setTabTips(prev => {
      if (!(tabId in prev)) return prev;
      const next = { ...prev }; delete next[tabId]; return next;
    });
  }, []);

  const openTab = useCallback((tabId) => {
    setActiveTab(prev => prev === tabId ? null : tabId); // toggle
  }, []);

  const closeDrawer = useCallback(() => {
    setActiveTab(null);
  }, []);

  const setTabTip = useCallback((tabId, tip) => {
    setTabTips(prev => {
      const nextTip = tip ?? null;
      if (tipsEqual(prev[tabId] ?? null, nextTip)) return prev;
      const next = { ...prev };
      if (nextTip) next[tabId] = nextTip; else delete next[tabId];
      return next;
    });
  }, []);

  const setWidthMode = useCallback((mode) => {
    if (!DRAWER_WIDTH_MODES.includes(mode)) return;
    setWidthModeState(mode);
    try { localStorage.setItem(DRAWER_WIDTH_MODE_KEY, mode); } catch { /* storage unavailable */ }
  }, []);

  const cycleWidthMode = useCallback(() => {
    setWidthMode(DRAWER_WIDTH_MODES[(DRAWER_WIDTH_MODES.indexOf(widthMode) + 1) % DRAWER_WIDTH_MODES.length]);
  }, [widthMode, setWidthMode]);

  const drawerWidthPx = DRAWER_WIDTHS[widthMode].px;

  return (
    <DrawerContext.Provider value={{
      tabs, activeTab, registerTab, unregisterTab, openTab, closeDrawer, tabTips, setTabTip,
      widthMode, setWidthMode, cycleWidthMode, drawerWidthPx,
    }}>
      {children}
    </DrawerContext.Provider>
  );
}

// ─── Styled Components ───────────────────────────────────────────

// Fixed-position to viewport — does NOT consume horizontal layout space.
// Prior `position: sticky` inside flex parent caused two problems:
//   1) rail moved with content because its scroll context wasn't the viewport
//   2) the empty 40px rail area exposed AppContainer's dark gradient on
//      light pages (the "black sidebar" effect on /raportti)
// Now: tab floats on the right edge of the viewport, page bg shows through
// behind it — works on dark workshop screens AND light report pages.
const DrawerContainer = styled.div`
  display: flex;
  position: fixed;
  /* MainViewHeader / AdminHeader publish --kuutio-admin-header-height.
     Falls back to 0 on pages without those headers (/raportti print, Storybook). */
  top: var(--kuutio-admin-header-height, 0px);
  right: 0;
  bottom: 0;
  z-index: 50;
  pointer-events: none; /* let clicks pass through empty space */
  & > * { pointer-events: auto; }
  @media print { display: none; }
`;

const TabBar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-top: 16px;
  padding-bottom: 4px;
`;

const Tab = styled.button`
  @keyframes spin { to { transform: rotate(360deg); } }
  width: ${TAB_BAR_WIDTH}px;
  height: 44px;
  /* Neutral semi-transparent greys so the rail works on both dark
     (workshop screens) and light (/raportti) page backgrounds. */
  background: ${props => props.$active
    ? `${props.$color || 'rgba(76, 175, 80, 0.3)'}`
    : 'rgba(127, 127, 127, 0.12)'};
  border: 1px solid ${props => props.$active
    ? `${props.$borderColor || 'rgba(76, 175, 80, 0.5)'}`
    : 'rgba(127, 127, 127, 0.25)'};
  border-right: none;
  border-radius: 2px 0 0 2px;
  color: ${props => props.$active ? 'white' : 'rgba(127, 127, 127, 0.9)'};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  backdrop-filter: blur(8px);

  &:hover {
    background: ${props => props.$active
      ? `${props.$color || 'rgba(76, 175, 80, 0.4)'}`
      : 'rgba(127, 127, 127, 0.22)'};
    color: ${props => props.$active ? 'white' : '#4b5563'};
  }

  svg {
    width: 18px;
    height: 18px;
  }

  position: relative;
`;

const TabDot = styled.span`
  position: absolute;
  top: 6px;
  right: 6px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${props => props.$color || '#c084fc'};
  pointer-events: none;
`;

function tipsEqual(a, b) {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (typeof a === 'string' || typeof b === 'string') return a === b;
  return a.text === b.text
    && a.id === b.id
    && a.dismissLabel === b.dismissLabel
    && a.closeLabel === b.closeLabel
    && a.idleDelay === b.idleDelay;
}

const Panel = styled.div`
  width: ${p => p.$open ? DRAWER_WIDTHS[p.$widthMode || 'normal'].css : '0'};
  --kuutio-drawer-pad: ${p => p.$widthMode === 'normal' ? '16px' : '24px'};
  --kuutio-chat-content-max: ${p => p.$widthMode === 'normal' ? 'none' : '820px'};
  --kuutio-chat-bubble-max: ${p => p.$widthMode === 'normal' ? '100%' : '760px'};
  /* DrawerContainer is top:0 bottom:0 — fill that box, not 100vh (avoids browser-chrome clipping). */
  height: 100%;
  /* Solid dark background — the drawer is a focused workspace surface,
     not a translucent overlay. Was transparent w/ blur, which let page
     content (report text/cards) bleed through behind the chat header. */
  background: #0a0a0a;
  border-left: ${p => p.$open ? '1px solid rgba(255, 255, 255, 0.08)' : 'none'};
  transition: width 0.3s ease;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  flex-shrink: 0;
`;

const PanelTitle = styled.h2`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: white;
  display: flex;
  align-items: center;
  gap: 8px;

  svg { width: 18px; height: 18px; }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const CloseButton = styled.button`
  width: 28px;
  height: 28px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 2px;
  color: rgba(255, 255, 255, 0.6);
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover { background: rgba(255, 255, 255, 0.15); color: white; }
`;

const WidthButton = styled(CloseButton)`
  svg { width: 14px; height: 14px; }
`;

// PanelBody is just a flex column — no scroll. TabContent owns scroll.
// Previously had overflow-y:auto here too, which chained with the inner
// chat MessageList scroll and caused "stupid" scroll behavior in the chat.
const PanelBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
`;

// Keep-alive wrapper — stays mounted but hidden when tab is inactive.
// overscroll-behavior:contain stops scroll-chaining out of the chat
// MessageList (and other inner scroll containers).
const TabContent = styled.div`
  display: ${props => props.$visible ? 'flex' : 'none'};
  flex-direction: column;
  flex: 1;
  /* Persistent tabs (chat) pin the input at the bottom; only MessageList scrolls. */
  overflow-y: ${props => props.$noOuterScroll ? 'hidden' : 'auto'};
  overscroll-behavior: contain;
  min-height: 0;
`;

// ─── DrawerShell — renders the tab bar + active panel ────────────
// Persistent tabs (chat) stay mounted via show/hide.
// Contextual tabs (pivot points etc.) render only when active.

export function DrawerShell() {
  // Soft-fail when context is missing (HMR can remount consumers against a
  // stale DrawerContext module instance). Prefer an empty drawer over a
  // full-page crash ("useDrawer must be used within <DrawerProvider>").
  // Hooks run before that early return, so their order never changes.
  const ctx = useContext(DrawerContext);
  const { tabs = [], activeTab, openTab, closeDrawer, tabTips, widthMode, cycleWidthMode } = ctx || {};
  const { t } = useTranslation();

  // Track which persistent tabs have been opened at least once
  // Use state (not ref) so adding a new tab triggers a re-render
  const [mountedTabs, setMountedTabs] = useState(new Set());
  useEffect(() => {
    if (activeTab) {
      const tab = tabs.find(t => t.id === activeTab);
      if (tab?.persistent && !mountedTabs.has(activeTab)) {
        setMountedTabs(prev => new Set([...prev, activeTab]));
      }
    }
  }, [activeTab, tabs, mountedTabs]);

  useEffect(() => {
    if (!cycleWidthMode) return undefined;
    const onKey = (e) => {
      if (!(e.metaKey || e.ctrlKey) || e.key !== '\\') return;
      e.preventDefault();
      cycleWidthMode();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [cycleWidthMode]);

  if (!ctx || tabs.length === 0) return null;

  const active = tabs.find(t => t.id === activeTab);
  const nextWidthMode = DRAWER_WIDTH_MODES[(DRAWER_WIDTH_MODES.indexOf(widthMode) + 1) % DRAWER_WIDTH_MODES.length];
  const WidthIcon = WIDTH_MODE_ICONS[widthMode] || PanelRight;
  const widthToggleLabel = t('drawer.widthMode.next', { mode: t(`drawer.widthMode.${nextWidthMode}`) });

  const persistentTabs = tabs.filter(t => t.persistent);
  const activeContextual = active && !active.persistent ? active : null;

  // Render via portal at document.body so the drawer is GUARANTEED to be a
  // direct child of <body>, immune to any ancestor that might create a new
  // containing block (transform, filter, contain, will-change). Without this,
  // position:fixed could resolve relative to a wrapper instead of the
  // viewport — making the chat appear to scroll with the page.
  const tree = (
    <DrawerContainer>
      <TabBar>
        {tabs.map(tab => {
          const rawTip = tabTips?.[tab.id];
          const showTip = rawTip && activeTab !== tab.id;
          const tip = typeof rawTip === 'string' ? { text: rawTip } : rawTip;
          const tabButton = (
            <Tab
              $active={activeTab === tab.id}
              $color={tab.color || 'rgba(76, 175, 80, 0.3)'}
              $borderColor={tab.borderColor || 'rgba(76, 175, 80, 0.5)'}
              onClick={() => openTab(tab.id)}
              title={tab.label}
            >
              {tab.icon}
              {tab.dot && <TabDot $color={tab.dot} />}
            </Tab>
          );
          if (!showTip) {
            return <React.Fragment key={tab.id}>{tabButton}</React.Fragment>;
          }
          return (
            <Tooltip
              key={tab.id}
              id={tip.id || `drawer.${tab.id}`}
              text={tip.text}
              trigger="idle"
              idleDelay={tip.idleDelay}
              placement="left"
              arrow
              interactive
              dismissLabel={tip.dismissLabel}
              closeLabel={tip.closeLabel}
              persistDismiss={tip.dismissLabel ? 'session' : 'none'}
              onClick={() => openTab(tab.id)}
            >
              {tabButton}
            </Tooltip>
          );
        })}
      </TabBar>

      <Panel $open={!!activeTab} $widthMode={widthMode}>
        {active && (
          <PanelHeader>
            <PanelTitle>
              {active.icon}
              {active.label}
            </PanelTitle>
            <HeaderActions>
              <WidthButton
                type="button"
                onClick={cycleWidthMode}
                title={widthToggleLabel}
                aria-label={widthToggleLabel}
              >
                <WidthIcon />
              </WidthButton>
              <CloseButton type="button" onClick={closeDrawer}>&times;</CloseButton>
            </HeaderActions>
          </PanelHeader>
        )}
        <PanelBody>
          {persistentTabs.map(tab => {
            if (!mountedTabs.has(tab.id)) return null;
            return (
              <TabContent key={tab.id} $visible={activeTab === tab.id} $noOuterScroll={tab.persistent}>
                {tab.render()}
              </TabContent>
            );
          })}
          {activeContextual && (
            <TabContent key={activeContextual.id} $visible>
              {activeContextual.render()}
            </TabContent>
          )}
        </PanelBody>
      </Panel>
    </DrawerContainer>
  );

  return typeof document !== 'undefined' ? createPortal(tree, document.body) : tree;
}
